import { useQuery } from 'react-query';
import { AxiosError } from 'axios';
import { client, Resource } from 'api';
import { useSelector } from 'react-redux';

// selectors
import getCurrentParentOrganization from 'store/selectors/getCurrentParentOrganization';

// helpers
import isNamesListSite from 'helpers/isNamesListSite';

// interfaces
import { ClassroomLinkItemProps } from 'layout/ApplicationLayout/components/ClassroomLinksButton/ClassroomLinkItem';

const DEFAULT_CLASSROOM_URLS = [
  { url: 'https://learn.phiredup.com/ifc21' },
  { url: 'https://learn.phiredup.com/cbf02021-2022' },
  { url: 'https://learn.phiredup.com/panhellenic' },
];

const CLASSROOMS_METADATA_MAP = {
  cbf: {
    title: 'Culturally-Based Fraternity/Sorority Growth Digital Classroom',
    description:
      'A comprehensive digital classroom to prepare culturally-based fraternity/sorority organization members to grow their chapters.',
    imageUrl:
      'https://learn.phiredup.com/content-assets/public/eyJhbGciOiJIUzI1NiJ9.eyJvYmplY3Rfa2V5IjoiVlpKUDI1Q1VQNGFHbWZXUEhRZzZ1TkJCIiwiZG9tYWluIjoibGVhcm4ucGhpcmVkdXAuY29tIn0.Qohi_HWpqFRdjdfhUAHFO0BtLOinmG1yuSPKMFv26n0',
    imageAlt: 'Culturally-Based Fraternity/Sorority Growth',
  },
  ifc: {
    title: 'IFC Fraternity Recruitment Digital Classroom',
    description:
      'A comprehensive digital classroom to prepare Phired Up Certified Fraternity Recruiters.',
    imageUrl:
      'https://learn.phiredup.com/content-assets/public/eyJhbGciOiJIUzI1NiJ9.eyJvYmplY3Rfa2V5IjoiSGVnamJEWjRKdloxRkVLTFM5RzFNWnJGIiwiZG9tYWluIjoibGVhcm4ucGhpcmVkdXAuY29tIn0.n3E9k-ggUNVxMaKvA4ITL3DalyNyjR49U9dgT9hSAv8',
    imageAlt: 'IFC Fraternity Recruitment',
  },
  panhellenic: {
    key: 'panhellenic',
    title: 'Panhellenic Recruitment Digital Classroom',
    description:
      'A comprehensive digital classroom to prepare Phired Up Certified Sorority Recruiters.',
    imageUrl:
      'https://learn.phiredup.com/content-assets/public/eyJhbGciOiJIUzI1NiJ9.eyJvYmplY3Rfa2V5IjoiWEZlN3lWaUJkR1BNbXVKZEc2V3dQUjVIIiwiZG9tYWluIjoibGVhcm4ucGhpcmVkdXAuY29tIn0.UncJQZ4no6kFC_7hjoHS7zCDf4pc2avU01KV9lADxos',
    imageAlt: 'Panhellenic Sorority Recruitment',
  },
  advisor: {
    title: 'Fraternity/Sorority Advisor Digital Classroom',
    description:
      'A comprehensive digital classroom to prepare "Phired Up Certified" Fraternity/Sorority Advisors in advising, coaching, and organizational growth principles.',
    imageUrl:
      'https://learn.phiredup.com/content-assets/public/eyJhbGciOiJIUzI1NiJ9.eyJvYmplY3Rfa2V5IjoiVGsxaE51ZFRua2h6M0pETWQ2Vkdod2ZTIiwiZG9tYWluIjoibGVhcm4ucGhpcmVkdXAuY29tIn0.IBCKlfOdUZ-UxfZ_7h0b2ivgjNLM1Xye69i9hd88PdE',
    imageAlt: 'Fraternity & Sorority Advisors',
  },
  cob: {
    title: 'COB Academy',
    description:
      'The most detailed classroom on earth for Panhellenic sorority COB-Style (Continous Open Bidding) recruitment. Everything you need to know to do COB with excellence is in this classroom',
    imageUrl:
      'https://learn.phiredup.com/content-assets/public/eyJhbGciOiJIUzI1NiJ9.eyJvYmplY3Rfa2V5IjoiUzZyS2dpclFndnJKczh4WUxZWnVLSlNCIiwiZG9tYWluIjoibGVhcm4ucGhpcmVkdXAuY29tIn0.PJ0hYmrLjUc_crIMlqpOryXXp8bXb_JzCjCiHFRZU2o',
    imageAlt: 'COB Academy',
  },
  councelor: {
    title: 'Recruitment Counselor Classroom',
    description:
      'As Recruitment Counselors you are often the first members of Panhellenic that PNMs build relationships with. You are challenged in ways that you may not have experienced before - difficult conversations, holding your friends accountable, and managing a process for hundreds of PNMs. We KNOW you can do this! Use this classroom as your guide to help you better understand and embrace your role!',
    imageUrl:
      'https://learn.phiredup.com/content-assets/public/eyJhbGciOiJIUzI1NiJ9.eyJvYmplY3Rfa2V5IjoidmdtN3poejIyZHN3eG05cml0dWZzN3ViY2k4ZCIsImRvbWFpbiI6ImxlYXJuLnBoaXJlZHVwLmNvbSJ9.uqFq25us08oaO4hVHQ20E01rh8FkYXErNGQeYiBrw5c',
    imageAlt: 'Recruitment Counselor Classroom',
  },
};

export function useClassroomLinksQuery() {
  const parentOrg = useSelector(getCurrentParentOrganization);
  const groupId = parentOrg?.id;
  const isNamesList = isNamesListSite();

  return useQuery<ClassroomLinkItemProps[], AxiosError>(
    [Resource.ClassroomLinks, groupId],
    () =>
      client
        .get(Resource.ClassroomLinks, { params: { groupId } })
        .then(({ data }) => data.results),
    {
      enabled: !isNamesList && Boolean(groupId),
      select: data => {
        const classroomLinkUrls = data.length ? data : DEFAULT_CLASSROOM_URLS;
        return classroomLinkUrls
          .map(classroomLink => {
            const matchedKey = Object.keys(
              CLASSROOMS_METADATA_MAP
            ).find((key: string) => classroomLink.url.includes(key));

            if (matchedKey) {
              const data =
                CLASSROOMS_METADATA_MAP[
                  matchedKey as keyof typeof CLASSROOMS_METADATA_MAP
                ];
              if (data) {
                return {
                  key: matchedKey,
                  url: classroomLink.url,
                  imageUrl: data.imageUrl,
                  imageAlt: data.imageAlt,
                  title: data.title,
                  description: data.description,
                };
              }
            }
            return null;
          })
          .filter((item): item is ClassroomLinkItemProps => item !== null);
      },
    }
  );
}
